import { useEffect, useRef, useState } from "react";
import { metricCharts } from "./metricCharts";
import { ChartPreviewContext } from "@/components/cheatsheet/ChartPreviewContext";

const PLACEHOLDER =
  "aspect-[3/2] w-full bg-muted/60 group-hover:scale-[1.03] transition-transform duration-500";

/**
 * Card thumbnail for a library post. For a metric that has a chart, renders a
 * compact, chrome-less live preview of that chart (via ChartPreviewContext);
 * otherwise falls back to the neutral placeholder tile.
 *
 * The chart mounts lazily — only once the tile scrolls into view — so a grid of
 * the full metric catalog stays cheap: dozens of cards, but only the visible few
 * ever render a live Recharts. Posts without a chart fall back to their hero
 * image when they have one, and to the plain tile otherwise.
 */
export const LibraryThumb = ({
  slug,
  image,
  live = false,
}: {
  slug: string;
  image?: string;
  live?: boolean;
}) => {
  const Chart = live ? metricCharts[slug] : undefined;
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!Chart) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [Chart]);

  if (!Chart) {
    if (image) {
      return (
        <img
          src={image}
          alt=""
          className="aspect-[3/2] w-full object-cover object-top bg-muted/40 group-hover:scale-[1.03] transition-transform duration-500"
        />
      );
    }
    return <div className={PLACEHOLDER} />;
  }

  return (
    <div
      ref={ref}
      className="aspect-[3/2] w-full overflow-hidden bg-muted/40 group-hover:scale-[1.03] transition-transform duration-500"
    >
      {visible && (
        <ChartPreviewContext.Provider value={true}>
          <div className="pointer-events-none h-full w-full bg-card p-3">
            <Chart />
          </div>
        </ChartPreviewContext.Provider>
      )}
    </div>
  );
};
